import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Handle, Position, useStore } from '@xyflow/react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import type { ProcessArtifactDto } from '@api/processes';
import { acceptedKindsOf, type Port } from '../graphTypes';
import { useNodeRun } from '../../hooks/useNodeRun';
import { useEditorContext } from '../useEditorContext';
import { useHeldNodeImage } from './useHeldNodeArtifacts';
import ImageModelSelect from './ImageModelSelect';
import { FieldLabel, GeneratingOverlay } from './nodeCommon.styled';
import {
  ActionButton,
  ActionOverlay,
  FloatingOutputPill,
  ImageEmptyState,
  ImageLabelRow,
  ImageSlot,
  InlineLabelText,
  InstructionRow,
  InstructionSection,
  InstructionTextarea,
  ModelSection,
  ResizeHandle,
  SlotImage,
} from './ImageGenNodeBody.styled';

/** SVG chevron matching OutputRowPill. */
const CHEVRON_SVG = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Placeholder icon shown when no image has been generated yet. */
const EMPTY_SVG = (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">
    <path
      d="M5 21C4.45 21 3.97917 20.8042 3.5875 20.4125C3.19583 20.0208 3 19.55 3 19V5C3 4.45 3.19583 3.97917 3.5875 3.5875C3.97917 3.19583 4.45 3 5 3H13V5H5V19H19V11H21V19C21 19.55 20.8042 20.0208 20.4125 20.4125C20.0208 20.8042 19.55 21 19 21H5ZM6 17H18L14.25 12L11.25 16L9 13L6 17ZM17 9V7H15V5H17V3H19V5H21V7H19V9H17Z"
      fill="currentColor"
    />
  </svg>
);

const MAX_SLOT = 720;

export interface HeroSize {
  width: number;
  height: number;
}

export interface ImageGenNodeBodyProps {
  readonly nodeId: string;
  /** Current instruction text (from node data). */
  readonly instruction: string;
  readonly onInstructionChange: (val: string) => void;
  /** Latest artifacts produced by this node. */
  readonly latestArtifacts: readonly ProcessArtifactDto[] | undefined;
  /** The single output port for the connection pill. */
  readonly outputPort: Port | undefined;
  /** User-set explicit slot dimensions (null = auto / natural size). */
  readonly heroSize: HeroSize | null;
  /** Natural slot size from the first-ever drag — the lower bound for resize. */
  readonly heroMinSize: HeroSize | null;
  /** Transient live size during drag. Owned by the view model so card width can track it. */
  readonly liveSize: HeroSize | null;
  readonly onLiveSizeChange: (next: HeroSize | null) => void;
  /** Called on drag-end to persist the new slot size; also drives node card width. */
  readonly onHeroSizeChange: (size: HeroSize, minSize: HeroSize) => void;
  /** Persisted fal.ai endpoint for text-to-image (blank = service default). */
  readonly genModel?: string;
  /** Persisted fal.ai endpoint for image editing (blank = service default). */
  readonly editModel?: string;
  /** Persist a model selection into the matching config field. */
  readonly onModelChange: (field: 'genModel' | 'editModel', value: string) => void;
  readonly readOnly?: boolean;
}

/**
 * True when at least one incoming edge originates from an output port that
 * emits IMAGE — meaning the node will edit a wired image rather than generate
 * from scratch, so the editor should offer image-to-image models.
 */
const useHasImageInput = (nodeId: string): boolean =>
  useStore((s) => {
    for (const edge of s.edges) {
      if (edge.target !== nodeId) continue;
      const source = s.nodeLookup.get(edge.source);
      const ports = (source?.data as { ports?: Port[] } | undefined)?.ports;
      const port = ports?.find((p) => p.portId === edge.sourceHandle);
      if (port && acceptedKindsOf(port).includes('IMAGE')) {
        return true;
      }
    }
    return false;
  });

/**
 * Custom body for IMAGE_GEN nodes — hero image slot, Download / Regenerate
 * hover actions, drag-to-resize corner handle, instruction textarea, and
 * floating output port pill.
 */
const ImageGenNodeBody: React.FC<ImageGenNodeBodyProps> = ({
  nodeId,
  instruction,
  onInstructionChange,
  latestArtifacts,
  outputPort,
  heroSize,
  heroMinSize,
  liveSize,
  onLiveSizeChange,
  onHeroSizeChange,
  genModel,
  editModel,
  onModelChange,
  readOnly,
}) => {
  const { start: regenerate, gate, hasWarning } = useNodeRun(nodeId);
  const { isNodeGenerating } = useEditorContext();
  const hasImageInput = useHasImageInput(nodeId);
  // Regenerate must respect validation the same way the header Run
  // button does — without this, a node with a broken incoming wire
  // (e.g. 3D-model output → image-gen input) could still be re-run via
  // the hover overlay, bypassing the warning surface on the header.
  const canRegenerate = gate.canRun && !hasWarning;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const zoom = useStore((s) => s.transform[2]);

  // Forward-only image selection: hold the image that's on screen and
  // only swap it for a fresh live image (or a same-artifact URL refresh).
  // This keeps a single-node regenerate from flashing back to an older
  // persisted snapshot during the window where the live map is cleared on
  // run start but the new image hasn't streamed in yet (AIB-7181).
  const imageArtifact = useHeldNodeImage(nodeId, latestArtifacts);
  const imageUrl = imageArtifact?.downloadUrl ?? null;
  const imageName = imageArtifact?.name ?? 'generated-image';
  const generating = isNodeGenerating(nodeId);

  const stopPropagation = useCallback(
    (e: React.SyntheticEvent) => e.stopPropagation(),
    [],
  );

  const handleDownload = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!imageUrl) return;
      const a = document.createElement('a');
      a.href = imageUrl;
      a.download = imageName;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [imageUrl, imageName],
  );

  const handleRegenerate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      regenerate();
    },
    [regenerate],
  );

  // Set immediately on drag commit; the synthetic click that fires after
  // pointerup checks this and bails. Cleared on the next macrotask so the
  // very next click (a real one) is honoured.
  const justDraggedRef = useRef(false);

  const handleSlotClick = useCallback(
    (e: React.MouseEvent) => {
      if (!imageUrl) return;
      if ((e.target as HTMLElement).closest('[data-image-actions]')) return;
      if ((e.target as HTMLElement).closest('[data-image-resize]')) return;
      if (justDraggedRef.current) {
        justDraggedRef.current = false;
        return;
      }
      e.stopPropagation();
      setLightboxOpen(true);
    },
    [imageUrl],
  );

  // ── Resize drag ──────────────────────────────────────────────────────────

  const slotRef = useRef<HTMLDivElement>(null);

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (readOnly) return;
      e.stopPropagation();
      e.preventDefault();

      const slot = slotRef.current;
      if (!slot) return;

      const button = e.currentTarget;
      const pointerId = e.pointerId;
      button.setPointerCapture(pointerId);

      const rect = slot.getBoundingClientRect();
      const currentZoom = zoom || 1;
      const startW = rect.width / currentZoom;
      const startH = rect.height / currentZoom;
      // Lock aspect ratio to the slot's current shape so a drag never
      // introduces letter-box bars.
      const aspectRatio = startH > 0 ? startW / startH : 1;
      // heroMinSize = natural size captured on first drag, reused as floor
      // so the user can always shrink back to the original unresized size.
      const initialMin = heroMinSize ?? { width: Math.round(startW), height: Math.round(startH) };
      const minW = initialMin.width;

      const startX = e.clientX;
      let rafId: number | null = null;
      // Track the latest computed size so any end-path can persist it.
      // Without this, capture loss mid-drag silently reverts the resize.
      let lastSize: { width: number; height: number } = {
        width: Math.round(startW),
        height: Math.round(startH),
      };
      let moved = false;
      let active = true;

      const cleanup = () => {
        if (!active) return;
        active = false;
        if (rafId !== null) cancelAnimationFrame(rafId);
        button.removeEventListener('pointermove', onMove);
        button.removeEventListener('pointerup', onUp);
        button.removeEventListener('pointercancel', onCancel);
        button.removeEventListener('lostpointercapture', onLost);
      };

      const commit = () => {
        // Always release the live override so the slot snaps back to its
        // persisted dims; only persist (and arm the click-suppress) when
        // the pointer actually moved — a bare click on the handle is a
        // no-op, not a resize.
        onLiveSizeChange(null);
        if (!moved) return;
        onHeroSizeChange(lastSize, initialMin);
        // Suppress the click that fires right after pointerup on the slot.
        justDraggedRef.current = true;
      };

      const onMove = (ev: PointerEvent) => {
        if (!active || ev.pointerId !== pointerId) return;
        const dx = (ev.clientX - startX) / currentZoom;
        const w = Math.round(Math.max(minW, Math.min(MAX_SLOT, startW + dx)));
        const h = Math.round(w / aspectRatio);
        lastSize = { width: w, height: h };
        moved = true;
        if (rafId !== null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          onLiveSizeChange({ width: w, height: h });
          rafId = null;
        });
      };

      const onUp = (ev: PointerEvent) => {
        if (!active || ev.pointerId !== pointerId) return;
        cleanup();
        commit();
      };

      // Browser explicitly cancelled the gesture (touch interruption, etc).
      // Revert rather than commit.
      const onCancel = (ev: PointerEvent) => {
        if (!active || ev.pointerId !== pointerId) return;
        cleanup();
        onLiveSizeChange(null);
      };

      // lostpointercapture is a backstop for cases where some other
      // element steals pointer capture mid-drag (React Flow canvas pan,
      // context menu, etc.) so pointerup never reaches us. Without this,
      // dragging past the node edge would silently revert.
      const onLost = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId || !active) return;
        cleanup();
        commit();
      };

      button.addEventListener('pointermove', onMove);
      button.addEventListener('pointerup', onUp);
      button.addEventListener('pointercancel', onCancel);
      button.addEventListener('lostpointercapture', onLost);
    },
    [readOnly, zoom, heroMinSize, onLiveSizeChange, onHeroSizeChange],
  );

  // ── Instruction buffer ───────────────────────────────────────────────────

  const [instrBuf, setInstrBuf] = useState(instruction);
  // Sync local buffer when the external value changes (e.g. undo/redo).
  useEffect(() => {
    setInstrBuf(instruction);
  }, [instruction]);

  // ── Slot size to apply ───────────────────────────────────────────────────

  // liveSize drives the slot during drag; heroSize takes over after drop.
  const activeSize = liveSize ?? heroSize;

  return (
    <div className="nodrag">
      {/* ── Image field ───────────────────────────────────────── */}
      <div>
        <ImageLabelRow>
          <InlineLabelText>Image</InlineLabelText>
        </ImageLabelRow>

        <ImageSlot
          ref={slotRef}
          $filled={!!imageUrl}
          $overrideWidth={activeSize?.width}
          $overrideHeight={activeSize?.height}
          role="img"
          aria-label={
            imageUrl
              ? imageName
              : 'Empty image preview — generated image will appear here'
          }
          onClick={handleSlotClick}
          data-testid={`process-node__image-slot-${nodeId}`}
        >
          {imageUrl && (
            <SlotImage
              src={imageUrl}
              alt={imageName}
              draggable={false}
              $sized={!!activeSize}
            />
          )}
          {!imageUrl && (
            <ImageEmptyState aria-hidden="true">{EMPTY_SVG}</ImageEmptyState>
          )}

          {generating && (
            <GeneratingOverlay
              aria-hidden="true"
              data-testid={`process-node__image-generating-${nodeId}`}
            >
              <span className="material-symbols-rounded">progress_activity</span>
            </GeneratingOverlay>
          )}

          {/* Hover overlay: Download + Regenerate */}
          <ActionOverlay data-image-actions>
            <ActionButton
              type="button"
              $disabled={!imageUrl}
              disabled={!imageUrl}
              title={imageUrl ? 'Download image' : 'No image to download yet'}
              aria-label="Download image"
              onPointerDown={stopPropagation}
              onClick={handleDownload}
            >
              <span className="material-symbols-rounded">download</span>
            </ActionButton>

            <ActionButton
              type="button"
              $disabled={!canRegenerate}
              disabled={!canRegenerate}
              title={
                hasWarning
                  ? 'Fix the validation errors on this node before regenerating.'
                  : 'Regenerate image'
              }
              aria-label="Regenerate image"
              onPointerDown={stopPropagation}
              onClick={handleRegenerate}
            >
              <span className="material-symbols-rounded">refresh</span>
            </ActionButton>
          </ActionOverlay>

          {!readOnly && (
            <ResizeHandle
              type="button"
              aria-label="Resize image slot"
              title="Drag to resize"
              data-image-resize
              onPointerDown={handleResizePointerDown}
            >
              <span className="material-symbols-rounded">drag_handle</span>
            </ResizeHandle>
          )}
        </ImageSlot>
      </div>

      {/* ── Model field ───────────────────────────────────────── */}
      <ModelSection>
        <ImageModelSelect
          mode={hasImageInput ? 'edit' : 'generate'}
          value={hasImageInput ? editModel : genModel}
          onChange={(id) =>
            onModelChange(hasImageInput ? 'editModel' : 'genModel', id)
          }
          readOnly={readOnly}
          testId={`process-node__image-model-${nodeId}`}
        />
      </ModelSection>

      {/* ── Instruction field ─────────────────────────────────── */}
      <InstructionSection>
        <FieldLabel>Instruction</FieldLabel>
        <InstructionRow>
          <InstructionTextarea
            value={instrBuf}
            placeholder="Generate an image of a design, in red color, make it modern and futuristic..."
            readOnly={readOnly}
            rows={3}
            onPointerDown={stopPropagation}
            onClick={stopPropagation}
            onChange={(e) => setInstrBuf(e.target.value)}
            onBlur={() => {
              if (!readOnly) onInstructionChange(instrBuf);
            }}
            data-testid={`process-node__instruction-${nodeId}`}
          />
          {outputPort && (
            <FloatingOutputPill
              data-testid={`process-node__handle-out-${nodeId}`}
            >
              {CHEVRON_SVG}
              <Handle
                type="source"
                position={Position.Right}
                id={outputPort.portId}
              />
            </FloatingOutputPill>
          )}
        </InstructionRow>
      </InstructionSection>

      {/* Lightbox for full-screen image preview. */}
      {imageUrl && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          slides={[{ src: imageUrl, alt: imageName }]}
          plugins={[Zoom]}
          carousel={{ finite: true }}
          controller={{ closeOnBackdropClick: true }}
          render={{ buttonPrev: () => null, buttonNext: () => null }}
        />
      )}
    </div>
  );
};

export default ImageGenNodeBody;
